import { useState, useEffect } from 'react';
import { Modal, Button, Input } from './ui';
import { validarVariacion, aplicarVariaciones } from '../utils/helpers';
import { TrendingDown, TrendingUp } from 'lucide-react';

/**
 * Modal para crear o editar UNA variación de precio (descuento o extra).
 *
 * Props:
 *   open       → boolean, controla apertura
 *   onClose    → callback al cerrar sin guardar
 *   base       → number, valor base sobre el que se aplica esta variacion.
 *                Se usa para preview y para validar descuento por monto.
 *   variacion  → objeto existente para editar, null para crear nueva.
 *   onSave     → callback(variacion) al confirmar. Retorna el objeto normalizado.
 *   moneda     → 'PEN' | 'USD'
 */
const formatMoney = (n, moneda) => {
    const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
    return symbol + (parseFloat(n) || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const VariacionFormModal = ({ open, onClose, base = 0, variacion = null, onSave, moneda = 'PEN' }) => {
    const [tipo, setTipo] = useState('descuento');
    const [modalidad, setModalidad] = useState('porcentaje');
    const [valor, setValor] = useState('');
    const [etiqueta, setEtiqueta] = useState('');
    const [error, setError] = useState('');

    // Reset al abrir (o rehidratar si es edicion)
    useEffect(() => {
        if (!open) return;
        if (variacion) {
            setTipo(variacion.tipo || 'descuento');
            setModalidad(variacion.modalidad || 'porcentaje');
            setValor(String(variacion.valor ?? ''));
            setEtiqueta(variacion.etiqueta || '');
        } else {
            setTipo('descuento');
            setModalidad('porcentaje');
            setValor('');
            setEtiqueta('');
        }
        setError('');
    }, [open, variacion]);

    const valorNum = parseFloat(valor) || 0;
    const preview = { tipo, modalidad, valor: valorNum, etiqueta };
    const resultado = valorNum > 0 ? aplicarVariaciones(base, [preview]) : base;
    const delta = resultado - base;

    const handleAplicar = () => {
        const check = validarVariacion(preview, base);
        if (!check.ok) {
            setError(check.message);
            return;
        }
        onSave({
            ...(variacion || {}),
            tipo,
            modalidad,
            valor: valorNum,
            etiqueta: etiqueta.trim() || null
        });
    };

    const rangeHint = tipo === 'descuento'
        ? (modalidad === 'porcentaje' ? '1% – 100%' : `1 – ${formatMoney(base, moneda)}`)
        : (modalidad === 'porcentaje' ? 'Mínimo 1%' : 'Mínimo 1');

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={variacion ? 'Editar variación' : 'Nueva variación'}
            size="sm"
            footer={
                <>
                    <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={handleAplicar} disabled={!valor}>
                        {variacion ? 'Guardar' : 'Aplicar'}
                    </Button>
                </>
            }
        >
            <div className="variacion-form">
                <div className="variacion-form-group">
                    <label className="variacion-form-label">Tipo</label>
                    <div className="variacion-toggle-group">
                        <button
                            type="button"
                            className={`variacion-toggle ${tipo === 'descuento' ? 'active variacion-toggle-danger' : ''}`}
                            onClick={() => setTipo('descuento')}
                        >
                            <TrendingDown size={14} />
                            Descuento
                        </button>
                        <button
                            type="button"
                            className={`variacion-toggle ${tipo === 'extra' ? 'active variacion-toggle-success' : ''}`}
                            onClick={() => setTipo('extra')}
                        >
                            <TrendingUp size={14} />
                            Extra
                        </button>
                    </div>
                </div>

                <div className="variacion-form-group">
                    <label className="variacion-form-label">Modalidad</label>
                    <div className="variacion-toggle-group">
                        <button
                            type="button"
                            className={`variacion-toggle ${modalidad === 'porcentaje' ? 'active' : ''}`}
                            onClick={() => setModalidad('porcentaje')}
                        >
                            Porcentaje (%)
                        </button>
                        <button
                            type="button"
                            className={`variacion-toggle ${modalidad === 'monto' ? 'active' : ''}`}
                            onClick={() => setModalidad('monto')}
                        >
                            Monto fijo
                        </button>
                    </div>
                </div>

                <div className="variacion-form-group">
                    <label className="variacion-form-label">
                        Valor <span className="variacion-form-hint">({rangeHint})</span>
                    </label>
                    <div className="variacion-input-with-suffix">
                        <Input
                            type="number"
                            value={valor}
                            onChange={(e) => { setValor(e.target.value); setError(''); }}
                            placeholder="0"
                            min="1"
                            step={modalidad === 'porcentaje' ? '0.1' : '0.01'}
                            autoFocus
                        />
                        <span className="variacion-input-suffix">
                            {modalidad === 'porcentaje' ? '%' : (moneda === 'USD' ? '$' : 'S/')}
                        </span>
                    </div>
                </div>

                <div className="variacion-form-group">
                    <label className="variacion-form-label">
                        Etiqueta <span className="variacion-form-hint">(opcional, uso interno)</span>
                    </label>
                    <Input
                        type="text"
                        value={etiqueta}
                        onChange={(e) => setEtiqueta(e.target.value)}
                        placeholder="Ej: Cliente frecuente, negociación, urgencia..."
                        maxLength={500}
                    />
                </div>

                {error && (
                    <div className="variacion-form-error">{error}</div>
                )}

                {/* Preview del efecto */}
                <div className="variacion-preview">
                    <div className="variacion-preview-row">
                        <span className="variacion-preview-label">Base:</span>
                        <span className="variacion-preview-value">{formatMoney(base, moneda)}</span>
                    </div>
                    <div className="variacion-preview-row">
                        <span className="variacion-preview-label">
                            {tipo === 'descuento' ? 'Descuento:' : 'Extra:'}
                        </span>
                        <span className={`variacion-preview-value ${tipo === 'descuento' ? 'variacion-danger' : 'variacion-success'}`}>
                            {tipo === 'descuento' ? '– ' : '+ '}{formatMoney(Math.abs(delta), moneda)}
                        </span>
                    </div>
                    <div className="variacion-preview-row variacion-preview-total">
                        <span className="variacion-preview-label">Resultado:</span>
                        <span className="variacion-preview-value">{formatMoney(resultado, moneda)}</span>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default VariacionFormModal;
